import html2pdf from "html2pdf.js";
import vajra_logo from "../../../assets/images/vajra_logo.svg";
import { getConsultantLogoUrl } from "../../../utils/consultantLogo";

export const generatePdf = async (reportRef) => {
  const element = reportRef.current;

  // Add custom page break handling
  const addCustomPageBreaks = () => {
    const pageBreakDivs = element.querySelectorAll('div[style*="pageBreakBefore"]');
    pageBreakDivs.forEach(div => {
      div.style.pageBreakBefore = 'always';
      div.style.breakBefore = 'page';
      div.style.height = '1px';
      div.style.margin = '0';
      div.style.padding = '0';
      div.style.display = 'block';
    });
  };

  // Call the function to ensure page breaks are properly set
  addCustomPageBreaks();

  const opt = {
    margin: [25, 10, 30, 10],
    filename: "Initial_Questionnaire.pdf",
    image: { type: "jpeg", quality: 0.98, background: "white" },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: true,
      backgroundColor: "#FFFFFF",
      removeContainer: true,
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    pagebreak: { mode: ["css", "legacy"] },
  };

  const convertSvgToPngBase64 = (svgPath) => {
    return new Promise((resolve) => {
      const img = new Image();
      // Needed once the source can be a remote consultant logo URL (not
      // just the bundled, same-origin vajra_logo asset) — without it,
      // canvas.toDataURL() throws a SecurityError on a cross-origin image.
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve({ dataUrl: canvas.toDataURL("image/png"), width: img.width, height: img.height });
        } catch (err) {
          // Cross-origin without the right CORS headers taints the canvas —
          // skip the logo rather than fail the whole PDF/submit.
          console.error("Logo canvas conversion failed:", err);
          resolve(null);
        }
      };
      // A blocked/missing/CORS-rejected image must never hang this promise
      // forever — that stalls generatePdf() and the submit flow along with it.
      img.onerror = () => {
        console.error("Logo image failed to load:", svgPath);
        resolve(null);
      };
      img.src = svgPath;
    });
  };

  // Consultant client with white-labeling set up -> their logo. Otherwise
  // the default Vajra logo.
  const consultantLogoUrl = getConsultantLogoUrl();
  // The sidebar already shows this same logo as a plain <img> (no Origin
  // header), and S3 answers that without CORS headers or Vary: Origin — so
  // the browser caches a CORS-less copy and reuses it for the crossOrigin
  // request below, which then gets blocked. A distinct query string forces
  // a fresh request that S3 answers with Access-Control-Allow-Origin.
  // (Not "?cors" — that's a reserved S3 sub-resource and returns 403.)
  const logo = await convertSvgToPngBase64(
    consultantLogoUrl
      ? `${consultantLogoUrl}${consultantLogoUrl.includes("?") ? "&" : "?"}pdf=1`
      : vajra_logo,
  );
  const pdf = await html2pdf().set(opt).from(element).toPdf().get("pdf");
  const totalPages = pdf.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);

    if (logo) {
      // Uploaded logos are 700x200px — at natural size that's ~185mm wide,
      // nearly the whole page. Fit into the box the default vajra_logo.svg
      // (126x36px) fills exactly, keeping aspect ratio, right edge and
      // vertical band, so a consultant logo lands exactly where Vajra's does.
      const pxToMm = 25.4 / 96;
      const boxWidth = 126 * pxToMm;
      const boxHeight = 36 * pxToMm;
      const scale = Math.min(boxWidth / logo.width, boxHeight / logo.height);
      const logoWidth = logo.width * scale;
      const logoHeight = logo.height * scale;

      // Right edge flush with the content's right margin (opt.margin[1]),
      // so it lines up with the section cards below.
      const xPosition = pdf.internal.pageSize.getWidth() - opt.margin[1] - logoWidth;
      const yPosition = 6 + (boxHeight - logoHeight) / 2;

      pdf.addImage(logo.dataUrl, "PNG", xPosition, yPosition, logoWidth, logoHeight);
    }
    if (i === 1) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(20);
      pdf.setTextColor(0, 0, 0);
      pdf.text(
        "Initial Questionnaire",
        pdf.internal.pageSize.getWidth() / 2,
        25,
        { align: "center" }
      );
    }

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text(
      `Page ${i} of ${totalPages}`,
      pdf.internal.pageSize.getWidth() / 2,
      pdf.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );
  }
  return pdf.output("blob");
};
